import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Megaphone, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { GenerateCampaignDialog } from '@/components/marketing/GenerateCampaignDialog';
import { CampaignDetail } from '@/components/marketing/CampaignDetail';
import { useCampaigns, useDeleteCampaign } from '@/hooks/data/useMarketing';

export const Route = createFileRoute('/_app/marketing')({
  component: MarketingPage,
});

function MarketingPage() {
  const { t } = useTranslation();
  const { data: campaigns = [], isLoading } = useCampaigns();
  const del = useDeleteCampaign();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const remove = async (id: string) => {
    const ok = await confirm({
      title: t('marketing.delete_title', 'Kampagne löschen?'),
      body: t('marketing.delete_body', 'Die Kampagne wird ausgeblendet.'),
      destructive: true,
    });
    if (!ok) return;
    await del.mutateAsync(id);
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">{t('marketing.title', 'Marketing')}</h2>
          <p className="text-sm text-ink-3">
            {t('marketing.subtitle', 'KI-generierter Marketing-Content für Ihre Immobilien.')}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          {t('marketing.new_campaign', 'Neue Kampagne')}
        </Button>
      </header>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-2">
          {isLoading && <p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>}
          {!isLoading && campaigns.length === 0 && (
            <div className="rounded-xl border border-dashed border-line p-6 text-center">
              <Megaphone className="mx-auto mb-2 h-6 w-6 text-ink-3" />
              <p className="text-sm text-ink-3">
                {t('marketing.empty', 'Noch keine Kampagnen. Erstellen Sie Ihre erste.')}
              </p>
            </div>
          )}
          {campaigns.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedId(c.id)}
              className={`flex w-full items-start justify-between gap-2 rounded-lg border p-3 text-start ${
                selectedId === c.id ? 'border-ink bg-paper-2' : 'border-line hover:bg-paper-2'
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-ink">{c.title}</span>
                <span className="text-[11px] text-ink-3">
                  {t(`marketing.type.${c.campaign_type}`, c.campaign_type)}
                </span>
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  void remove(c.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    void remove(c.id);
                  }
                }}
                className="rounded p-1 text-ink-3 hover:bg-paper-3 hover:text-[hsl(var(--danger))]"
                aria-label={t('common.delete', 'Löschen')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </span>
            </button>
          ))}
        </div>

        <div>
          {selectedId ? (
            <CampaignDetail campaignId={selectedId} />
          ) : (
            <div className="rounded-xl border border-line p-8 text-center text-sm text-ink-3">
              {t('marketing.select_hint', 'Wählen Sie links eine Kampagne oder erstellen Sie eine neue.')}
            </div>
          )}
        </div>
      </div>

      <GenerateCampaignDialog open={creating} onOpenChange={setCreating} onCreated={setSelectedId} />
    </section>
  );
}
